import React, { ReactElement, ReactNode, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Image } from 'components/lib';
import {
  COLORS,
  FONT,
  MaterialSymbol,
  ViewIcon,
  ManagerIcon,
  JoinIcon,
  SkeletonBlock,
  initialsOf,
} from '../../shared';

const CREST_SIZE = 100;

export type OrgBrowseRole = 'admin' | 'leader' | 'member' | 'follower' | 'none';

export interface OrgBrowseTileProps {
  name: string;
  logoUrl?: string;
  role: OrgBrowseRole;

  isRequested?: boolean;
  onView: () => void;

  onManage?: () => void;

  onJoin?: () => void;
  joining?: boolean;
}

const ROLE_BADGE: Record<Exclude<OrgBrowseRole, 'none'>, string> = {
  admin: 'Admin',
  leader: 'Leader',
  member: 'Member',
  follower: 'Following',
};

const ToolbarAction = ({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}): ReactElement => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    disableRipple
    sx={{
      flex: '1 0 0',
      minWidth: 0,
      height: '48px',
      gap: '6px',
      color: COLORS.navy,
      opacity: disabled ? 0.5 : 1,
      '&:hover': {
        backgroundColor: COLORS.hoverRow,
        '& .org-browse-action-label': { textDecoration: 'underline' },
      },
      '&:focus-visible': {
        outline: `2px solid ${COLORS.navy}`,
        outlineOffset: '-2px',
      },
    }}
  >
    {icon}
    <Typography
      className="org-browse-action-label"
      sx={{
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: 1.32,
        color: COLORS.navy,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

export const OrgBrowseTile = ({
  name,
  logoUrl,
  role,
  isRequested = false,
  onView,
  onManage,
  onJoin,
  joining = false,
}: OrgBrowseTileProps): ReactElement => {
  const [logoError, setLogoError] = useState(false);
  const showLogo = logoUrl && !logoError;

  const canManage =
    (role === 'admin' || role === 'leader') && Boolean(onManage);
  const canJoin =
    !isRequested && (role === 'follower' || role === 'none') && Boolean(onJoin);

  return (
    <Box
      sx={{
        backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.cardBorder}`,
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        height: '162px',
        pt: '18px',
        pb: '14px',
        px: '16px',
        width: '100%',
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease',
        '@media (hover: hover)': {
          '&:hover': { boxShadow: '0 4px 16px rgba(20,31,51,0.08)' },
        },
      }}
    >
      <ButtonBase
        onClick={onView}
        disableRipple
        aria-label={`View ${name}`}
        sx={{
          width: `${CREST_SIZE}px`,
          height: `${CREST_SIZE}px`,
          borderRadius: '12px',
          backgroundColor: COLORS.imagePlaceholder,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: '2px',
          },
        }}
      >
        {showLogo ? (
          <Image
            src={logoUrl ?? ''}
            alt={name}
            width={CREST_SIZE}
            height={CREST_SIZE}
            onError={() => setLogoError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography
            aria-hidden
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '32px',
              color: COLORS.navy,
            }}
          >
            {initialsOf(name)}
          </Typography>
        )}
      </ButtonBase>

      <Box
        sx={{
          flex: '1 0 0',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignSelf: 'stretch',
          justifyContent: 'flex-start',
        }}
      >
        {isRequested ? (
          <Box
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: COLORS.pillBg,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              px: '8px',
              py: '4px',
              borderRadius: '9999px',
            }}
          >
            <MaterialSymbol name="schedule" size={16} color={COLORS.bodyDark} />
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                color: COLORS.bodyDark,
                whiteSpace: 'nowrap',
              }}
            >
              Pending request
            </Typography>
          </Box>
        ) : role !== 'none' ? (
          <Box
            sx={{
              alignSelf: 'flex-start',
              backgroundColor: COLORS.pillBg,
              display: 'inline-flex',
              alignItems: 'center',
              px: '8px',
              py: '4px',
              borderRadius: '9999px',
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                color: COLORS.bodyDark,
                whiteSpace: 'nowrap',
              }}
            >
              {ROLE_BADGE[role]}
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <Typography
            component={ButtonBase}
            onClick={onView}
            disableRipple
            sx={{
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: 1.32,
              color: COLORS.headingDark,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': {
                outline: `2px solid ${COLORS.navy}`,
                outlineOffset: '2px',
              },
            }}
          >
            {name}
          </Typography>

          <Box
            sx={{
              backgroundColor: COLORS.toolbarBg,
              display: 'flex',
              alignItems: 'center',
              gap: canManage || canJoin ? '8px' : 0,
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <ToolbarAction
              icon={<ViewIcon color={COLORS.navy} />}
              label="View"
              onClick={onView}
            />
            {canManage && (
              <>
                <Box
                  sx={{
                    width: '1px',
                    height: '14px',
                    backgroundColor: COLORS.divider,
                    flexShrink: 0,
                  }}
                />
                <ToolbarAction
                  icon={<ManagerIcon color={COLORS.navy} />}
                  label="Manage"
                  onClick={onManage}
                />
              </>
            )}
            {canJoin && (
              <>
                <Box
                  sx={{
                    width: '1px',
                    height: '14px',
                    backgroundColor: COLORS.divider,
                    flexShrink: 0,
                  }}
                />
                <ToolbarAction
                  icon={<JoinIcon color={COLORS.navy} />}
                  label="Join"
                  onClick={onJoin}
                  disabled={joining}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const OrgBrowseTileSkeleton = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.cardBorder}`,
      display: 'flex',
      gap: '12px',
      alignItems: 'center',
      height: '162px',
      pt: '18px',
      pb: '14px',
      px: '16px',
      width: '100%',
    }}
  >
    <SkeletonBlock
      width={100}
      height={100}
      radius={12}
      sx={{ flexShrink: 0 }}
    />
    <Box flex="1 0 0" display="flex" flexDirection="column" gap="8px">
      <SkeletonBlock width={56} height={22} radius={9999} />
      <SkeletonBlock width="80%" height={18} />
      <SkeletonBlock width="100%" height={48} />
    </Box>
  </Box>
);
